import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { LoginInput } from "@akhshy/types";
import { api, setUnauthorizedHandler } from "./api.ts";
import { getSession, saveSession, clearSession } from "./token.ts";

type Status = "checking" | "authenticated" | "unauthenticated";

interface AuthApi {
  status: Status;
  isAuthenticated: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");

  useEffect(() => {
    setUnauthorizedHandler(() => setStatus("unauthenticated"));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    const session = getSession();
    if (!session) {
      setStatus("unauthenticated");
      return;
    }
    api.auth
      .me()
      .then(() => setStatus("authenticated"))
      .catch(() => {
        clearSession();
        setStatus("unauthenticated");
      });
  }, []);

  const login = async (input: LoginInput) => {
    const res = await api.auth.login(input);
    saveSession(res.token, res.expiresAt);
    setStatus("authenticated");
  };

  const logout = () => {
    clearSession();
    setStatus("unauthenticated");
  };

  const value: AuthApi = {
    status,
    isAuthenticated: status === "authenticated",
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
